import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import './SymbolChart.css';

// Enregistrer les composants Chart.js nécessaires
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const SymbolChart = () => {
  const { token, logout } = useAuth();
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [updatingHistory, setUpdatingHistory] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [stats, setStats] = useState(null);
  const [updateMessage, setUpdateMessage] = useState(null);

  const loadChartData = async (symbolToLoad) => {
    if (!symbolToLoad || !symbolToLoad.trim()) {
      setError('Veuillez entrer un symbole');
      return;
    }

    setLoading(true);
    setError(null);
    setChartData(null);
    setStats(null);

    try {
      // Calculer la date de début (30 jours avant aujourd'hui)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      const symbolUpper = symbolToLoad.toUpperCase().trim();
      const url = `/api/alpaca/history/${encodeURIComponent(symbolUpper)}?start_date=${startDateStr}&end_date=${endDateStr}`;

      console.log('[SymbolChart] Fetching data from:', url);

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(url, { headers });

      if (response.status === 401) {
        await logout();
        return;
      }

      // Vérifier le Content-Type avant de parser
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('[SymbolChart] Réponse non-JSON reçue:', {
          status: response.status,
          statusText: response.statusText,
          contentType,
          url,
          preview: text.substring(0, 500)
        });
        throw new Error(`Réponse invalide du serveur (${response.status} ${response.statusText}). L'endpoint pourrait ne pas exister ou retourner une erreur HTML.`);
      }

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || data.error || 'Erreur lors du chargement des données');
      }

      if (!data.data || data.data.length === 0) {
        setError(`Aucune donnée historique trouvée pour ${symbolToLoad.toUpperCase()} sur la période ${startDateStr} à ${endDateStr}. Utilisez le bouton "Mettre à jour l'historique" ci-dessous pour récupérer les données depuis Alpaca.`);
        return;
      }

      // Afficher un avertissement si peu de données
      if (data.data.length < 10) {
        setUpdateMessage(`ℹ️ Seulement ${data.data.length} jour(s) de données trouvé(s) pour ${symbolToLoad.toUpperCase()}. Utilisez le bouton "Mettre à jour l'historique" pour récupérer 30 jours de données depuis Alpaca.`);
      } else {
        setUpdateMessage(null);
      }

      // Trier les données par date (croissant)
      const sortedData = [...data.data].sort((a, b) => 
        new Date(a.trade_date) - new Date(b.trade_date)
      );

      // Préparer les données pour le graphique
      const labels = sortedData.map(item => {
        const date = new Date(item.trade_date);
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
      });

      const closePrices = sortedData.map(item => parseFloat(item.close || 0));
      const volumes = sortedData.map(item => parseFloat(item.volume || 0));

      setChartData({
        labels,
        datasets: [
          {
            label: 'Prix de clôture ($)',
            data: closePrices,
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: true,
            tension: 0.4,
            yAxisID: 'y',
          },
          {
            label: 'Volume',
            data: volumes,
            borderColor: 'rgb(139, 92, 246)',
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            fill: true,
            tension: 0.4,
            yAxisID: 'y1',
            hidden: true, // Masqué par défaut
          }
        ]
      });

      // Calculer les stats supplémentaires si nécessaire
      const statsData = data.stats || {};
      if (sortedData.length > 0) {
        const firstPrice = parseFloat(sortedData[0].close || 0);
        const lastPrice = parseFloat(sortedData[sortedData.length - 1].close || 0);
        const priceChange = firstPrice > 0 ? ((lastPrice - firstPrice) / firstPrice) * 100 : 0;
        
        setStats({
          min_price: statsData.min_close || statsData.min_price || 0,
          max_price: statsData.max_close || statsData.max_price || 0,
          avg_price: statsData.avg_close || statsData.avg_price || 0,
          price_change: priceChange,
          total_days: sortedData.length,
          first_date: sortedData[0].trade_date,
          last_date: sortedData[sortedData.length - 1].trade_date
        });
      } else {
        setStats(statsData);
      }

    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
      setError(err.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    loadChartData(symbol);
  };

  const handleUpdateHistory = async () => {
    if (!symbol || !symbol.trim()) {
      setError('Veuillez entrer un symbole');
      return;
    }

    setUpdatingHistory(true);
    setUpdateMessage(null);
    setError(null);

    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch('/api/alpaca/history/update', {
        method: 'POST',
        headers,
        body: JSON.stringify({ symbols: [symbol.toUpperCase().trim()] })
      });

      if (response.status === 401) {
        await logout();
        return;
      }

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error || 'Erreur lors de la mise à jour');
      }

      const inserted = data.inserted || 0;
      const skipped = data.skipped || 0;
      setUpdateMessage(`✅ Historique mis à jour : ${inserted} barres insérées, ${skipped} ignorées. Rechargez le graphique pour voir les nouvelles données.`);

      // Recharger automatiquement les données après la mise à jour
      setTimeout(() => {
        loadChartData(symbol);
      }, 1000);

    } catch (err) {
      console.error('Erreur lors de la mise à jour de l\'historique:', err);
      setError(err.message || 'Erreur lors de la mise à jour de l\'historique');
    } finally {
      setUpdatingHistory(false);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `Évolution du prix sur 30 jours - ${symbol.toUpperCase()}`,
        font: {
          size: 18,
          weight: 'bold'
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Prix ($)'
        }
      },
      y1: {
        type: 'linear',
        display: false,
        position: 'right',
        title: {
          display: true,
          text: 'Volume'
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  return (
    <div className="symbol-chart-page">
      <div className="symbol-chart-header">
        <h1 className="symbol-chart-title">📈 Graphique d'actif</h1>
        <p className="symbol-chart-subtitle">Visualisez l'évolution d'un symbole sur 30 jours</p>
      </div>

      <div className="symbol-chart-form-container">
        <form onSubmit={handleSubmit} className="symbol-chart-form">
          <div className="form-group">
            <label htmlFor="symbol">Symbole</label>
            <input
              type="text"
              id="symbol"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              placeholder="Ex: AAPL, TSLA, MSFT"
              className="symbol-input"
              maxLength={10}
              required
            />
          </div>
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Chargement...' : 'Afficher le graphique'}
          </button>
        </form>
        {symbol && (
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button 
              type="button" 
              onClick={handleUpdateHistory} 
              className="update-history-btn" 
              disabled={updatingHistory || loading}
            >
              {updatingHistory ? 'Mise à jour...' : '📥 Mettre à jour l\'historique (30 jours)'}
            </button>
          </div>
        )}
      </div>

      {updateMessage && (
        <div className="success-message">
          {updateMessage}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {stats && (
        <div className="stats-container">
          <h2>Statistiques</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Prix minimum</div>
              <div className="stat-value">${parseFloat(stats.min_price || 0).toFixed(2)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Prix maximum</div>
              <div className="stat-value">${parseFloat(stats.max_price || 0).toFixed(2)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Prix moyen</div>
              <div className="stat-value">${parseFloat(stats.avg_price || 0).toFixed(2)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Variation</div>
              <div className={`stat-value ${parseFloat(stats.price_change || 0) >= 0 ? 'positive' : 'negative'}`}>
                {parseFloat(stats.price_change || 0) >= 0 ? '+' : ''}
                {parseFloat(stats.price_change || 0).toFixed(2)}%
              </div>
            </div>
          </div>
        </div>
      )}

      {chartData && (
        <div className="chart-container">
          <div className="chart-wrapper">
            <Line data={chartData} options={chartOptions} />
          </div>
        </div>
      )}
    </div>
  );
};

export default SymbolChart;
